import { useState, useCallback } from 'react'
import { NumberLimitItem } from './NumberLimitModal'

interface LimitDialogOptions {
  onConfirm?: () => void
  onCancel?: () => void
}

interface LimitDialogState {
  isOpen: boolean
  data: NumberLimitItem[]
  type: 'limit' | 'restricted'
  onConfirm?: () => void
  onCancel?: () => void
}

// Types from useBetControl
interface HitData {
  data: NumberLimitItem[]
  type: 'limit' | 'restricted'
}

export const useLimitDialog = () => {
  const [dialogState, setDialogState] = useState<LimitDialogState>({
    isOpen: false,
    data: [],
    type: 'limit',
  })

  const show = useCallback(
    (data: NumberLimitItem[], options: LimitDialogOptions = {}) => {
      setDialogState({
        isOpen: true,
        data,
        type: 'limit',
        onConfirm: options.onConfirm,
        onCancel: options.onCancel,
      })
    },
    [],
  )

  const showRestricted = useCallback(
    (data: NumberLimitItem[], options: LimitDialogOptions = {}) => {
      setDialogState({
        isOpen: true,
        data,
        type: 'restricted',
        onConfirm: options.onConfirm,
        onCancel: options.onCancel,
      })
    },
    [],
  )

  // Main method to show dialog with HitData
  const showFromHitData = useCallback(
    (hitData: HitData, options: LimitDialogOptions = {}) => {
      if (hitData.data.length > 0) {
        const showMethod = hitData.type === 'restricted' ? showRestricted : show
        showMethod(hitData.data, options)
      }
    },
    [show, showRestricted],
  )

  const hide = useCallback(() => {
    setDialogState((prev) => ({
      ...prev,
      isOpen: false,
    }))
  }, [])

  const handleConfirm = useCallback(() => {
    if (dialogState.onConfirm) {
      dialogState.onConfirm()
    }
    hide()
  }, [dialogState, hide])

  const handleCancel = useCallback(() => {
    if (dialogState.onCancel) {
      dialogState.onCancel()
    }
    hide()
  }, [dialogState, hide])

  return {
    show,
    showRestricted,
    showFromHitData,
    hide,
    dialogState,
    handleConfirm,
    handleCancel,
  }
}

// Convenience function for direct usage without hook
export const createLimitDialog = () => {
  let resolvePromise: (value: boolean) => void

  const show = (): Promise<boolean> => {
    return new Promise((resolve) => {
      resolvePromise = resolve

      // This would need to be integrated with a global dialog manager
      // For now, this is a placeholder for the API structure
      // Show limit dialog with data
    })
  }

  const confirm = () => {
    if (resolvePromise) {
      resolvePromise(true)
    }
  }

  const cancel = () => {
    if (resolvePromise) {
      resolvePromise(false)
    }
  }

  return {
    show,
    confirm,
    cancel,
  }
}
